import React, { useEffect, useState } from 'react';
import { Modal, Button, Input, Select } from './UI';
import { useData } from '../App';
import { CompanyProfile, TransactionStatus, TransactionType, TransferBasis } from '../types';
import { api } from '../services/api';
import { accountLabel, orgDisplayName, todayISO } from '../utils/format';

const BASES: { value: TransferBasis; label: string }[] = [
    { value: 'LOAN', label: 'Заём' },
    { value: 'LOAN_REPAYMENT', label: 'Возврат займа' },
    { value: 'OWN_FUNDS', label: 'Внесение собственных средств (один владелец)' },
];

const walletLabel = (w: CompanyProfile['accounts'][number]) => w.kind === 'cash' ? (w.name || 'Наличные') : accountLabel(w);

// Кому выводим: себе (владельцу ИП) или другой организации аккаунта.
// Вывод себе — с подвидом (payoutLabel): бонус → статья партнёра, ЗП партнёра → «ЗП», премия → «Премия»
const SELF = '__self';
const SELF_KINDS: { value: string; label: string; payoutLabel: 'SALARY' | 'BONUS' | null; title: string }[] = [
    { value: SELF, label: 'Себе — бонус партнёра', payoutLabel: null, title: 'Вывод себе' },
    { value: `${SELF}:SALARY`, label: 'Себе — ЗП партнёра', payoutLabel: 'SALARY', title: 'ЗП партнёра' },
    { value: `${SELF}:BONUS`, label: 'Себе — премия партнёра', payoutLabel: 'BONUS', title: 'Премия партнёра' },
];

// «Вывод»: себе — операция «Вывод себе» (уменьшает баланс, не расход и не налоги);
// другому ИП — перевод между организациями (не доход и не расход, двигает деньги между кошельками)
export const TransferForm: React.FC<{ isOpen: boolean; onClose: () => void; orgs: CompanyProfile[]; fromOrgId: string | null }> =
    ({ isOpen, onClose, orgs, fromOrgId }) => {
    const { refreshData, showNotification, addTransaction } = useData();
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [fromWallet, setFromWallet] = useState('');
    const [toWallet, setToWallet] = useState('');
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(todayISO());
    const [basis, setBasis] = useState<TransferBasis>('LOAN');
    const [description, setDescription] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        const f = fromOrgId || orgs[0]?.id || '';
        const fOrg = orgs.find(o => o.id === f);
        setFrom(f); setTo(fOrg?.type === 'ENTREPRENEUR' ? f : orgs.find(o => o.id !== f)?.id || '');
        setAmount(''); setDate(todayISO()); setBasis('LOAN'); setDescription('');
    }, [isOpen, fromOrgId, orgs]);
    const src = orgs.find(o => o.id === from), dst = orgs.find(o => o.id === to);
    const selfKind = SELF_KINDS.find(k => k.value === to);
    const toSelf = !!selfKind;
    // Та же организация — перемещение между своими счетами и кошельком (снял наличные с карты, внёс на счёт)
    const own = !!from && to === from;
    const targets = [{ value: from, label: 'Свой счёт / кошелёк (перемещение)' },
                     ...(src?.type === 'ENTREPRENEUR' ? SELF_KINDS.map(k => ({ value: k.value, label: k.label })) : []),
                     ...orgs.filter(o => o.id !== from).map(o => ({ value: o.id, label: orgDisplayName(o) }))];
    const changeFrom = (id: string) => {
        setFrom(id);
        const o = orgs.find(x => x.id === id);
        if (to === from || to === id || (toSelf && o?.type !== 'ENTREPRENEUR')) setTo(o?.type === 'ENTREPRENEUR' ? id : orgs.find(x => x.id !== id)?.id || '');
    };
    useEffect(() => { setFromWallet(src?.activeAccountId || src?.accounts[0]?.id || ''); }, [src]);
    useEffect(() => {
        // Перемещение: по умолчанию со счёта в кошелёк «Наличные / карта»
        if (own) setToWallet(dst?.accounts.find(w => w.kind === 'cash' && w.id !== fromWallet)?.id || dst?.accounts.find(w => w.id !== fromWallet)?.id || '');
        else setToWallet(dst?.activeAccountId || dst?.accounts[0]?.id || '');
    }, [dst, own, fromWallet]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (toSelf) {
            const wallet = src?.accounts.find(w => w.id === fromWallet);
            addTransaction({ organizationId: from, type: TransactionType.EXPENSE, kind: 'OWNER_WITHDRAWAL', date, amount: Number(amount),
                             category: 'Вывод себе', description: description || selfKind!.title, status: TransactionStatus.PAID,
                             payoutLabel: selfKind!.payoutLabel,
                             hasVat: false, vatRate: 0, items: [], hasDocument: true,
                             paymentMethod: wallet?.kind === 'cash' ? 'cash' : 'bank', bankAccountId: wallet?.kind === 'cash' ? null : fromWallet || null });
            onClose();
            return;
        }
        if (!from || !to) { showNotification('Выберите, откуда и куда', 'error'); return; }
        if (own && (!fromWallet || fromWallet === toWallet)) { showNotification('Выберите два разных счёта / кошелька', 'error'); return; }
        try {
            await api.createTransfer({ fromOrgId: from, toOrgId: to, amount: Number(amount), date, basis,
                                       fromWalletId: fromWallet || null, toWalletId: toWallet || null, description });
            refreshData();
            onClose();
        } catch (err: any) { showNotification(err.message || 'Ошибка перевода', 'error'); }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Вывод / перемещение">
            <form onSubmit={submit} className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                    <Select label="Откуда" value={from} onChange={e => changeFrom(e.target.value)} options={orgs.map(o => ({ value: o.id, label: orgDisplayName(o) }))} />
                    <Select label="Кому" value={to} onChange={e => setTo(e.target.value)} options={targets} />
                    <Select label="Со счёта / кассы" value={fromWallet} onChange={e => setFromWallet(e.target.value)} options={(src?.accounts || []).map(w => ({ value: w.id, label: walletLabel(w) }))} />
                    {!toSelf && <Select label="На счёт / в кассу" value={toWallet} onChange={e => setToWallet(e.target.value)} options={(dst?.accounts || []).map(w => ({ value: w.id, label: walletLabel(w) }))} />}
                    <Input label="Сумма" type="number" min={0} step="0.01" value={amount} onChange={e => setAmount(e.target.value)} required />
                    <Input label="Дата" type="date" value={date} onChange={e => setDate(e.target.value)} required />
                </div>
                {!toSelf && !own && <Select label="Основание" value={basis} onChange={e => setBasis(e.target.value as TransferBasis)} options={BASES} />}
                <Input label="Комментарий" value={description} onChange={e => setDescription(e.target.value)}
                    placeholder={toSelf ? 'Например: на личные нужды' : 'Например: зарплата Архипову за октябрь'} />
                <p className="text-[11px] text-slate-500">{own
                    ? 'Перемещение: деньги остаются у ИП — со счёта в кошелёк «Наличные / карта» (снятие наличных, перевод себе на карту) или обратно. Не доход и не расход, доля партнёра не уменьшается.'
                    : toSelf && selfKind!.payoutLabel === 'SALARY'
                    ? 'ЗП партнёра: уменьшает баланс ИП, в планировании идёт в статью «ЗП» — бонус партнёра не уменьшает. Не расход и не влияет на налоги.'
                    : toSelf && selfKind!.payoutLabel === 'BONUS'
                    ? 'Премия партнёра: уменьшает баланс ИП, в планировании идёт в статью «Премия» — бонус партнёра не уменьшает. Не расход и не влияет на налоги.'
                    : toSelf
                    ? 'Вывод себе — бонус партнёра: уменьшает баланс ИП и долю партнёра в планировании, но не является расходом и не влияет на налоги. Если вы просто сняли наличные — выберите «Свой счёт / кошелёк».'
                    : 'Перевод между ИП — не доход и не расход: в налоги, сводку и планирование не входит. Займы видны во «Взаимных долгах».'}</p>
                <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button type="button" variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
                    <Button type="submit" size="sm">Провести</Button>
                </div>
            </form>
        </Modal>
    );
};
